import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Collapse } from "react-bootstrap";
import { SvgSprite } from "../components/SvgSprite";

const SectionTogglerContext = createContext<{
  expanded: boolean;
  toggleCollapse: () => void;
  collapseRef: React.RefObject<HTMLDivElement>;
} | null>(null);

export const useSectionToggler = () => {
  const context = useContext(SectionTogglerContext);
  if (!context) throw new Error("Must be used inside SectionToggler");
  return context;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const toggleCollapse = useCallback(() => setExpanded((state) => !state), []);
  const collapseRef = useRef<HTMLDivElement>(null);

  return (
    <SectionTogglerContext.Provider
      value={{ expanded, toggleCollapse, collapseRef }}
    >
      {children}
    </SectionTogglerContext.Provider>
  );
};

const Toggler = ({ title }: { title: string }) => {
  const { expanded, toggleCollapse, collapseRef } = useSectionToggler();

  useEffect(() => {
    if (expanded && collapseRef.current) {
      // Find the first focusable element within the collapse content
      const focusableElements = collapseRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      let idx = 0;
      let elementToFocus: HTMLElement = null;
      while (!elementToFocus && idx < focusableElements.length) {
        if (focusableElements[idx].getAttribute("type") !== "hidden") {
          elementToFocus = focusableElements[idx] as HTMLElement;
        }
        idx++;
      }

      setTimeout(() => elementToFocus?.focus(), 0);
    }
  }, [expanded, collapseRef]);

  return (
    <Button
      onClick={toggleCollapse}
      aria-controls="collapse-text"
      aria-expanded={expanded}
      variant="link"
      css={`
        border: unset;
        border-radius: unset;
        padding: 0.25rem;
        margin: 0 -0.25rem 0.25rem;
      `}
    >
      {title}
      <SvgSprite
        id="icons:chev-down"
        width={12}
        height={12}
        css={`
          margin-left: 0.25rem;
          transition: transform 0.3s ${(p): string => p.theme.timingFnEaseOut};
        `}
        style={{
          transform: expanded
            ? "rotate3d(1,0,0,180deg)"
            : "rotate3d(0,0,0,0deg)",
        }}
      />
    </Button>
  );
};

const Content = ({ children }: { children: React.ReactNode }) => {
  const { expanded, collapseRef } = useSectionToggler();

  return (
    <Collapse
      in={expanded}
      css={`
        transition: all 0.3s ${(p): string => p.theme.timingFnEaseOut};
        &.collapse.show {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        &.collapsing,
        &.collapse {
          opacity: 0;
          transform: translate3d(0, -10px, 0);
        }
      `}
    >
      <div id="collapse-text" ref={collapseRef}>
        {children}
      </div>
    </Collapse>
  );
};

export const SectionToggler = Object.assign(Wrapper, {
  Toggler,
  Content,
});
